import type {
  RealtimeMediaParticipantContract,
  RealtimeMediaSessionMetadata,
  RealtimeRecipientDescriptor,
  RealtimeSessionControlStateContract,
  RealtimeSessionLegContract,
  RealtimeSessionUiHintContract,
  RealtimeVoiceFallbackContract,
  RealtimeVoiceProviderClientConnectionContract,
  RealtimeVoiceProviderSessionEvidenceContract,
} from '@automatick/shared-ng';

export type RealtimeMediaControlMode =
  'none' | 'auto' | 'manual' | 'paused' | 'canceled' | 'requested';

export interface RealtimeMediaSessionView {
  readonly mediaSessionId: string;
  readonly title: string | null;
  readonly phase:
    'pending' | 'calling' | 'connecting' | 'connected' | 'ended' | 'error';
  readonly direction: 'outbound' | 'inbound';
  readonly requestedMediaKinds: readonly string[];
  readonly preferredSourceKinds: readonly string[];
  readonly metadata: RealtimeMediaSessionMetadata | null;
  readonly sessionType: string | null;
  readonly controlMode: RealtimeMediaControlMode;
  readonly targetRecipients: readonly RealtimeRecipientDescriptor[];
  readonly replyRecipients: readonly RealtimeRecipientDescriptor[];
  readonly localStream: MediaStream | null;
  readonly remoteStream: MediaStream | null;
  readonly providerSession: RealtimeVoiceProviderSessionEvidenceContract | null;
  readonly providerClientConnection: RealtimeVoiceProviderClientConnectionContract | null;
  readonly fallback: RealtimeVoiceFallbackContract | null;
  readonly errorMessage: string | null;
}

export function createRealtimeMediaSessionSnapshot(
  view: RealtimeMediaSessionView,
): RealtimeMediaSessionView {
  return Object.freeze({
    ...view,
    requestedMediaKinds: freezeArray(view.requestedMediaKinds),
    preferredSourceKinds: freezeArray(view.preferredSourceKinds),
    targetRecipients: freezeObjects(view.targetRecipients),
    replyRecipients: freezeObjects(view.replyRecipients),
    metadata: freezeMetadata(view.metadata),
    providerSession: view.providerSession
      ? freezeProviderEvidence(view.providerSession)
      : null,
    providerClientConnection: view.providerClientConnection
      ? freezeProviderConnection(view.providerClientConnection)
      : null,
    fallback: view.fallback ? freezeObject(view.fallback) : null,
  });
}

function freezeArray<T>(values: readonly T[]): T[] {
  const clone = [...values];
  Object.freeze(clone);
  return clone;
}

function freezeObjects<T extends object>(values: readonly T[]): T[] {
  return freezeArray(values.map(freezeObject));
}

function freezeObject<T extends object>(value: T): T {
  return Object.freeze({ ...value });
}

function freezeOptionalArray<T>(
  values: readonly T[] | null | undefined,
): T[] | null | undefined {
  return values ? freezeArray(values) : values;
}

function freezeOptionalObjects<T extends object>(
  values: readonly T[] | null | undefined,
): T[] | null | undefined {
  return values ? freezeObjects(values) : values;
}

function freezeOptionalMapped<T>(
  values: readonly T[] | null | undefined,
  project: (value: T) => T,
): T[] | null | undefined {
  return values ? freezeArray(values.map(project)) : values;
}

function freezeMetadata(
  metadata: RealtimeMediaSessionMetadata | null,
): RealtimeMediaSessionMetadata | null {
  if (!metadata) return null;
  return Object.freeze({
    ...metadata,
    requestedMediaKinds: freezeOptionalArray(metadata.requestedMediaKinds),
    preferredSourceKinds: freezeOptionalArray(metadata.preferredSourceKinds),
    participants: freezeOptionalMapped(
      metadata.participants,
      freezeParticipant,
    ),
    tracks: freezeOptionalObjects(metadata.tracks),
    legs: freezeOptionalMapped(metadata.legs, freezeLeg),
    bridgeLegs: freezeOptionalObjects(metadata.bridgeLegs),
    deviceRoles: freezeOptionalObjects(metadata.deviceRoles),
    uiHints: freezeOptionalMapped(metadata.uiHints, freezeUiHint),
    controlPermissions: freezeOptionalObjects(metadata.controlPermissions),
    controlState: metadata.controlState
      ? freezeControlState(metadata.controlState)
      : metadata.controlState,
    bridgeStates: freezeOptionalObjects(metadata.bridgeStates),
    lifecycleOutcome: metadata.lifecycleOutcome
      ? freezeObject(metadata.lifecycleOutcome)
      : metadata.lifecycleOutcome,
    realtimeVoiceProvider: metadata.realtimeVoiceProvider
      ? freezeProviderEvidence(metadata.realtimeVoiceProvider)
      : metadata.realtimeVoiceProvider,
    realtimeVoiceFallback: metadata.realtimeVoiceFallback
      ? freezeObject(metadata.realtimeVoiceFallback)
      : metadata.realtimeVoiceFallback,
  });
}

function freezeParticipant(
  participant: RealtimeMediaParticipantContract,
): RealtimeMediaParticipantContract {
  return Object.freeze({
    ...participant,
    recipient: participant.recipient
      ? freezeObject(participant.recipient)
      : participant.recipient,
    requestedMediaKinds: freezeOptionalArray(participant.requestedMediaKinds),
    availableMediaKinds: freezeOptionalArray(participant.availableMediaKinds),
  });
}

function freezeLeg(
  leg: RealtimeSessionLegContract,
): RealtimeSessionLegContract {
  return Object.freeze({
    ...leg,
    requestedMediaKinds: freezeOptionalArray(leg.requestedMediaKinds),
    preferredSourceKinds: freezeOptionalArray(leg.preferredSourceKinds),
  });
}

function freezeUiHint(
  hint: RealtimeSessionUiHintContract,
): RealtimeSessionUiHintContract {
  return Object.freeze({
    ...hint,
    availableActions: freezeOptionalArray(hint.availableActions),
  });
}

function freezeControlState(
  state: RealtimeSessionControlStateContract,
): RealtimeSessionControlStateContract {
  return Object.freeze({
    ...state,
    allowedInputKinds: freezeOptionalArray(state.allowedInputKinds),
  });
}

function freezeProviderEvidence(
  evidence: RealtimeVoiceProviderSessionEvidenceContract,
): RealtimeVoiceProviderSessionEvidenceContract {
  const endpointSelection = evidence.endpointSelection;
  const mediaAttachment = evidence.mediaAttachment;
  return Object.freeze({
    ...evidence,
    endpointSelection: endpointSelection
      ? Object.freeze({
          ...endpointSelection,
          requestedMediaKinds: freezeOptionalArray(
            endpointSelection.requestedMediaKinds,
          ),
        })
      : endpointSelection,
    mediaAttachment: mediaAttachment
      ? Object.freeze({
          ...mediaAttachment,
          requestedMediaKinds: freezeOptionalArray(
            mediaAttachment.requestedMediaKinds,
          ),
          modalities: freezeOptionalArray(mediaAttachment.modalities),
        })
      : mediaAttachment,
  });
}

function freezeProviderConnection(
  connection: RealtimeVoiceProviderClientConnectionContract,
): RealtimeVoiceProviderClientConnectionContract {
  return Object.freeze({
    ...connection,
    modalities: freezeOptionalArray(connection.modalities),
  });
}
